import { FocusTrap } from 'focus-trap-vue';
import PortalVue from 'portal-vue';
import Vue from 'vue';
import VueCookies from 'vue-cookies';
import VueLazyload from 'vue-lazyload';
import Vue2TouchEvents from 'vue2-touch-events';
import '@lottiefiles/lottie-player';

import VetsterSearchNav from '@/brand-components/GuidedSearch/VetsterSearchNav';
import VetsterButton from '@/brand-components/VetsterButton';
import VetsterInput from '@/brand-components/VetsterInput';
import VetsterNav from '@/brand-components/VetsterNav';
import VetsterGuidedSearch from '@/components/GuidedSearch/VetsterGuidedSearch';
import VetsterButtonLegacy from '@/components/VetsterButtonLegacy';
import VetsterInputLegacy from '@/components/VetsterInputLegacy';
import useCookies from '@/composables/use-cookies';
import useKeyboardEvents from '@/composables/use-keyboard-events';
import usePvarsStorage from '@/composables/use-pvars-storage';
import registerFontAwesomeSearch from '@/config/fontAwesomeSearch';
import registerVueTippy from '@/config/tooltips';
import { getCookie } from '@/helpers/cookies';
import { isDevelopment } from '@/helpers/environment';
import storeDeviceData from '@/helpers/storeDeviceData';
import { i18n } from '@/i18n';
import TogglesSupportChat from '@/mixins/TogglesSupportChat';
import Analytics from '@/packages/Analytics';
import Api from '@/packages/Api';
import Auth from '@/packages/Auth';
import { fullStoryReadyCallback } from '@/packages/FullStory';
import Sentry from '@/packages/Sentry';
import { initStatsig } from '@/packages/Statsig';
import EventBus from '@/plugins/EventBus';
import i18nFallback from '@/plugins/i18n';
import Layout from '@/plugins/Layout';
import Responsive from '@/plugins/Responsive';
import store from '@/store';

registerFontAwesomeSearch();
registerVueTippy();

if (isDevelopment()) {
  Vue.component('VetsterDebug', () => import('./components/VetsterDebug'));
}

Vue.component('VetsterToast', () => import('./brand-components/VetsterToast'));

Vue.component('VetsterAuthModalLegacy', () => import('./components/VetsterAuthModalLegacy'));
Vue.component('VetsterAuthFormsLegacy', () => import('./components/VetsterAuthFormsLegacy')); // ensures lazy loading of google auth
Vue.component('VetsterAuthSlideOver', () => import('./brand-components/VetsterAuthSlideOver'));
Vue.component('VetsterConfirmation', () => import('./brand-components/VetsterConfirmation'));
Vue.component('VetsterQuestionnaire', () => import('./brand-components/Questionnaire/VetsterQuestionnaire'));

// Load global plugins.
Vue.use(Analytics);
Vue.use(EventBus);
Vue.use(i18nFallback, { i18n });
Vue.use(Layout);
Vue.use(Responsive);
Vue.use(VueCookies);
Vue.use(VueLazyload, {
  observer: true,
});
Vue.use(Sentry);
Vue.use(Vue2TouchEvents);
Vue.use(PortalVue);

const uat = getCookie('v-uatid');

Vue.use(Api, { uat });
Vue.use(Auth, { uat });

initStatsig(Vue, uat);

// Globally-accessible components (use sparingly).
Vue.component('VetsterButtonLegacy', VetsterButtonLegacy);
Vue.component('VetsterInput', VetsterInput);
Vue.component('VetsterInputLegacy', VetsterInputLegacy);
Vue.component('VetsterButton', VetsterButton);
Vue.component('FocusTrap', FocusTrap);

// Log the user's device info.
storeDeviceData();

// eslint-disable-next-line no-new
new Vue({
  el: '#app',

  name: 'GuidedSearch',

  components: {
    VetsterGuidedSearch,
    VetsterNav,
    VetsterSearchNav,
  },

  mixins: [TogglesSupportChat],

  setup() {
    const {
      getViMessage,
      hasViMessage,
      openCookiePreferences,
      removeViMessage,
    } = useCookies();
    const { emitCloseOnEscape } = useKeyboardEvents();
    const { latestPvars } = usePvarsStorage();

    return {
      emitCloseOnEscape,
      getViMessage,
      hasViMessage,
      latestPvars,
      openCookiePreferences,
      removeViMessage,
    };
  },

  store,

  i18n,

  created() {
    store.$app = this;
    store.state.$auth = this.$auth;

    this.emitCloseOnEscape();
    this.$analytics.trackUAT(uat);
    this.$analytics.trackPageLoad();
    fullStoryReadyCallback(this.$sentry);
  },
});
